/**
 * StakeholderMap
 *
 * A component for visualizing stakeholder relationships and influence
 *
 * Primary responsibilities:
 * - Map relationships between stakeholders in a business/project context
 * - Visualize influence levels and relationship types
 * - Allow exploration of stakeholder networks and dependencies
 * - Support different mapping formats (grid, radial, etc.)
 */

import React, { useState } from 'react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import {
  IconUsers,
  IconStar,
  IconBuildingCommunity,
  IconUserCircle,
  IconChevronDown,
  IconChevronRight,
  IconArrowAutofitContent
} from '@tabler/icons-react'

// Individual stakeholder
export interface Stakeholder {
  id: string
  name: string
  type: 'individual' | 'organization' | 'group' | 'government' | 'community' | string
  description?: string
  interests?: string[]
  requirements?: string[]
  power?: number // 1-10 scale
  interest?: number // 1-10 scale
  influence?: number // 1-10 scale
  support?: number // -5 to 5 scale (negative = against)
  persona?: { [key: string]: string }
  image?: string
  metrics?: { [key: string]: any }
}

// Relationship between stakeholders
export interface StakeholderRelationship {
  source: string // ID of source stakeholder
  target: string // ID of target stakeholder
  type: 'collaboration' | 'dependency' | 'influence' | 'conflict' | 'support' | string
  strength: number // 1-10 scale
  description?: string
  bidirectional?: boolean
  metrics?: { [key: string]: any }
}

// Group of stakeholders
export interface StakeholderGroup {
  id: string
  name: string
  description?: string
  color?: string
  stakeholderIds: string[]
}

// Main stakeholder map data
export interface StakeholderMapData {
  title: string
  description?: string
  stakeholders: Stakeholder[]
  relationships: StakeholderRelationship[]
  groups?: StakeholderGroup[]
  view?: 'grid' | 'matrix' | 'power-interest' | 'list'
}

interface StakeholderMapProps {
  data: StakeholderMapData
  className?: string
}

export const StakeholderMap: React.FC<StakeholderMapProps> = ({ data, className }) => {
  const [expandedStakeholders, setExpandedStakeholders] = useState<Set<string>>(new Set())
  const [selectedStakeholder, setSelectedStakeholder] = useState<string | null>(null)
  const [currentView, setCurrentView] = useState<StakeholderMapData['view']>(data.view || 'list')

  // Toggle stakeholder expansion
  const toggleStakeholder = (id: string) => {
    const newExpandedStakeholders = new Set(expandedStakeholders)
    if (newExpandedStakeholders.has(id)) {
      newExpandedStakeholders.delete(id)
    } else {
      newExpandedStakeholders.add(id)
    }
    setExpandedStakeholders(newExpandedStakeholders)
  }

  // Set selected stakeholder
  const handleSelect = (id: string) => {
    setSelectedStakeholder((prevId) => (prevId === id ? null : id))
  }

  // Get stakeholder icon based on type
  const getStakeholderIcon = (type: Stakeholder['type']) => {
    switch (type) {
      case 'individual':
        return <IconUserCircle size={16} className="text-blue-500" />
      case 'organization':
        return <IconBuildingCommunity size={16} className="text-emerald-500" />
      case 'government':
        return <IconBuildingCommunity size={16} className="text-purple-500" />
      case 'group':
        return <IconUsers size={16} className="text-amber-500" />
      case 'community':
        return <IconUsers size={16} className="text-red-500" />
      default:
        return <IconUserCircle size={16} className="text-slate-500" />
    }
  }

  // Find relationships for a stakeholder
  const getStakeholderRelationships = (id: string) => {
    return data.relationships.filter((rel) => rel.source === id || rel.target === id)
  }

  // Get related stakeholders
  const getRelatedStakeholders = (id: string) => {
    const relationships = getStakeholderRelationships(id)
    const relatedIds = relationships.map((rel) => (rel.source === id ? rel.target : rel.source))
    return data.stakeholders.filter((s) => relatedIds.includes(s.id))
  }

  // Get stakeholder group
  const getStakeholderGroup = (id: string) => {
    return data.groups?.find((group) => group.stakeholderIds.includes(id))
  }

  // Get stakeholder power-interest quadrant
  const getQuadrant = (stakeholder: Stakeholder) => {
    if (!stakeholder.power || !stakeholder.interest) return null

    if (stakeholder.power > 5 && stakeholder.interest > 5) {
      return 'Key Players'
    } else if (stakeholder.power > 5 && stakeholder.interest <= 5) {
      return 'Keep Satisfied'
    } else if (stakeholder.power <= 5 && stakeholder.interest > 5) {
      return 'Keep Informed'
    } else {
      return 'Monitor'
    }
  }

  // Get relationship strength description
  const getRelationshipStrength = (strength: number) => {
    if (strength >= 8) return 'Strong'
    if (strength >= 5) return 'Moderate'
    return 'Weak'
  }

  // Get relationship type icon
  const getRelationshipTypeIcon = (type: StakeholderRelationship['type']) => {
    switch (type) {
      case 'collaboration':
        return '👥'
      case 'dependency':
        return '🔄'
      case 'influence':
        return '🔱'
      case 'conflict':
        return '⚡'
      case 'support':
        return '🤝'
      default:
        return '🔗'
    }
  }

  // Render power-interest grid
  const renderPowerInterestGrid = () => {
    // Filter stakeholders with power and interest values
    const validStakeholders = data.stakeholders.filter(
      (s) => s.power !== undefined && s.interest !== undefined
    )

    return (
      <div className="relative h-[400px] w-full rounded border">
        {/* Grid lines and labels */}
        <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 p-4">
          {/* Quadrant borders */}
          <div className="col-span-2 border-b border-dashed"></div>
          <div className="row-span-2 border-r border-dashed"></div>

          {/* Quadrant labels */}
          <div className="relative">
            <div className="text-muted-foreground absolute left-0 top-0 p-2 text-xs font-medium">
              Keep Satisfied
            </div>
          </div>
          <div className="relative">
            <div className="text-muted-foreground absolute right-0 top-0 p-2 text-xs font-medium">
              Key Players
            </div>
          </div>
          <div className="relative">
            <div className="text-muted-foreground absolute bottom-0 left-0 p-2 text-xs font-medium">
              Monitor
            </div>
          </div>
          <div className="relative">
            <div className="text-muted-foreground absolute bottom-0 right-0 p-2 text-xs font-medium">
              Keep Informed
            </div>
          </div>
        </div>

        {/* Axis labels */}
        <div className="text-muted-foreground absolute bottom-1 w-full text-center text-xs">
          Interest →
        </div>
        <div className="absolute left-1 flex h-full items-center">
          <div className="text-muted-foreground -rotate-90 whitespace-nowrap text-xs">Power →</div>
        </div>

        {/* Plot stakeholders */}
        {validStakeholders.map((stakeholder) => {
          const x = ((stakeholder.interest || 0) / 10) * 100
          const y = 100 - ((stakeholder.power || 0) / 10) * 100
          const isSelected = selectedStakeholder === stakeholder.id

          // Determine size based on influence
          const size = stakeholder.influence ? 15 + (stakeholder.influence / 10) * 20 : 20

          // Determine color based on group or support
          let color = '#6b7280' // Default gray
          if (stakeholder.support !== undefined) {
            if (stakeholder.support > 3)
              color = '#22c55e' // Green for strong support
            else if (stakeholder.support > 0)
              color = '#84cc16' // Lime for moderate support
            else if (stakeholder.support < -3)
              color = '#ef4444' // Red for strong opposition
            else if (stakeholder.support < 0) color = '#f97316' // Orange for moderate opposition
          }

          // Check if stakeholder belongs to a group
          const group = getStakeholderGroup(stakeholder.id)
          if (group?.color) {
            color = group.color
          }

          return (
            <div
              key={stakeholder.id}
              className={cn(
                'absolute flex -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full transition-all',
                isSelected ? 'ring-2 ring-black ring-offset-2 dark:ring-white' : ''
              )}
              style={{
                left: `${x}%`,
                top: `${y}%`,
                width: `${size}px`,
                height: `${size}px`,
                backgroundColor: color,
                opacity: isSelected || !selectedStakeholder ? 1 : 0.5
              }}
              onClick={() => handleSelect(stakeholder.id)}
              title={stakeholder.name}
            >
              <span className="text-[8px] font-bold text-white">
                {stakeholder.name.slice(0, 2).toUpperCase()}
              </span>
            </div>
          )
        })}
      </div>
    )
  }

  // Render stakeholder list
  const renderStakeholderList = () => {
    return (
      <div className="space-y-2">
        {data.stakeholders.map((stakeholder) => {
          const isExpanded = expandedStakeholders.has(stakeholder.id)
          const isSelected = selectedStakeholder === stakeholder.id
          const group = getStakeholderGroup(stakeholder.id)

          return (
            <div
              key={stakeholder.id}
              className={cn(
                'rounded-md border transition-colors',
                isSelected ? 'border-blue-400 bg-blue-50/50 dark:bg-blue-900/10' : '',
                !isSelected && !selectedStakeholder
                  ? 'hover:bg-muted/30'
                  : isSelected
                    ? ''
                    : 'opacity-70'
              )}
            >
              <div
                className="flex cursor-pointer items-center justify-between p-3"
                onClick={() => toggleStakeholder(stakeholder.id)}
              >
                <div className="flex items-center gap-2">
                  {getStakeholderIcon(stakeholder.type)}
                  <span className="text-sm font-medium">{stakeholder.name}</span>
                  {group && (
                    <span
                      className="rounded-full px-2 py-0.5 text-xs"
                      style={{
                        backgroundColor: group.color ? `${group.color}30` : '#f3f4f6',
                        color: group.color || 'currentColor'
                      }}
                    >
                      {group.name}
                    </span>
                  )}
                  {stakeholder.power !== undefined && stakeholder.interest !== undefined && (
                    <span className="text-muted-foreground ml-2 text-xs">
                      {getQuadrant(stakeholder)}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="size-6"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleSelect(stakeholder.id)
                    }}
                    title={isSelected ? 'Deselect' : 'Select'}
                  >
                    <IconArrowAutofitContent size={14} />
                  </Button>
                  {getStakeholderRelationships(stakeholder.id).length > 0 &&
                    (isExpanded ? (
                      <IconChevronDown size={16} className="text-muted-foreground" />
                    ) : (
                      <IconChevronRight size={16} className="text-muted-foreground" />
                    ))}
                </div>
              </div>

              {isExpanded && (
                <div className="px-3 pb-3">
                  {stakeholder.description && (
                    <p className="text-muted-foreground mb-2 text-xs">{stakeholder.description}</p>
                  )}

                  <div className="mb-3 grid grid-cols-2 gap-x-4 gap-y-2">
                    {stakeholder.power !== undefined && (
                      <div>
                        <div className="text-xs font-medium">Power</div>
                        <div className="bg-muted/30 relative h-1.5 w-full rounded-full">
                          <div
                            className="absolute inset-y-0 left-0 rounded-full bg-blue-500"
                            style={{ width: `${(stakeholder.power / 10) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {stakeholder.interest !== undefined && (
                      <div>
                        <div className="text-xs font-medium">Interest</div>
                        <div className="bg-muted/30 relative h-1.5 w-full rounded-full">
                          <div
                            className="absolute inset-y-0 left-0 rounded-full bg-emerald-500"
                            style={{ width: `${(stakeholder.interest / 10) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {stakeholder.influence !== undefined && (
                      <div>
                        <div className="text-xs font-medium">Influence</div>
                        <div className="bg-muted/30 relative h-1.5 w-full rounded-full">
                          <div
                            className="absolute inset-y-0 left-0 rounded-full bg-purple-500"
                            style={{ width: `${(stakeholder.influence / 10) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                    )}

                    {stakeholder.support !== undefined && (
                      <div>
                        <div className="text-xs font-medium">Support</div>
                        <div className="bg-muted/30 relative h-1.5 w-full overflow-hidden rounded-full">
                          <div
                            className={cn(
                              'absolute inset-y-0 rounded-full',
                              stakeholder.support > 0
                                ? 'left-1/2 bg-green-500'
                                : 'right-1/2 bg-red-500'
                            )}
                            style={{
                              width: `${Math.abs(stakeholder.support / 5) * 50}%`
                            }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>

                  {stakeholder.interests && stakeholder.interests.length > 0 && (
                    <div className="mb-2">
                      <div className="mb-1 text-xs font-medium">Interests</div>
                      <ul className="text-muted-foreground list-disc pl-5 text-xs">
                        {stakeholder.interests.map((interest, i) => (
                          <li key={`interest-${i}`}>{interest}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {stakeholder.requirements && stakeholder.requirements.length > 0 && (
                    <div className="mb-2">
                      <div className="mb-1 text-xs font-medium">Requirements</div>
                      <ul className="text-muted-foreground list-disc pl-5 text-xs">
                        {stakeholder.requirements.map((req, i) => (
                          <li key={`req-${i}`}>{req}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {getStakeholderRelationships(stakeholder.id).length > 0 && (
                    <div className="mt-3">
                      <div className="mb-1 text-xs font-medium">Relationships</div>
                      <div className="space-y-2">
                        {getStakeholderRelationships(stakeholder.id).map((relationship) => {
                          const isSource = relationship.source === stakeholder.id
                          const otherStakeholderId = isSource
                            ? relationship.target
                            : relationship.source
                          const otherStakeholder = data.stakeholders.find(
                            (s) => s.id === otherStakeholderId
                          )
                          if (!otherStakeholder) return null

                          return (
                            <div
                              key={`rel-${relationship.source}-${relationship.target}`}
                              className="flex items-center gap-2 pl-1 text-xs"
                            >
                              <span className="text-base">
                                {getRelationshipTypeIcon(relationship.type)}
                              </span>

                              <span
                                className="cursor-pointer font-medium hover:underline"
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleSelect(otherStakeholderId)
                                }}
                              >
                                {otherStakeholder.name}
                              </span>

                              <span className="text-muted-foreground">
                                {isSource
                                  ? relationship.bidirectional
                                    ? '↔'
                                    : '→'
                                  : relationship.bidirectional
                                    ? '↔'
                                    : '←'}{' '}
                                {relationship.type} (
                                {getRelationshipStrength(relationship.strength)})
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <div
      className={cn(
        'border-border bg-card my-4 flex flex-col overflow-hidden rounded-lg border shadow-sm transition-all',
        className
      )}
    >
      <div className="flex items-center justify-between border-b p-3">
        <div className="flex items-center gap-2">
          <IconUsers size={16} className="text-muted-foreground" />
          <h3 className="text-sm font-medium">{data.title}</h3>
        </div>

        <div className="flex items-center gap-2">
          {selectedStakeholder && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={() => setSelectedStakeholder(null)}
            >
              Clear Selection
            </Button>
          )}

          <select
            value={currentView}
            onChange={(e) => setCurrentView(e.target.value as StakeholderMapData['view'])}
            className="rounded border px-2 py-1 text-xs"
          >
            <option value="list">List View</option>
            <option value="power-interest">Power-Interest Grid</option>
            {/* Other view options could be added */}
          </select>
        </div>
      </div>

      {data.description && (
        <div className="text-muted-foreground border-b px-4 py-2 text-xs">{data.description}</div>
      )}

      <div className="max-h-[600px] overflow-y-auto p-4">
        {currentView === 'power-interest' ? renderPowerInterestGrid() : renderStakeholderList()}
      </div>
    </div>
  )
}
